import React from "react";
import Pagination from "../commons/Pagination";
import SkeletonCard from "../skeletons/SkeletonCard";
import { TrashIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";

const TradeHistoryTable = ({
  activeTab,
  setActiveTab,
  data,
  loading,
  error,
  onDeleteClick,
  currentPage,
  setCurrentPage,
  totalPages,
  toggleRowExpansion,
  expandedRow,
  isMobile,
}) => {
  const desktopHeaders = [
    "Date & Time",
    "Pair",
    "Type",
    "Side",
    "Price",
    "Amount",
    "Total",
    "Action",
  ];

  return (
    <div className="bg-white p-6 rounded-xl border border-gray-200 w-full">
      <h2 className="text-xl font-bold text-gray-800">Trade History</h2>

      {/* Tabs */}
      <div className="mt-4">
        <nav className="flex -mb-px gap-6">
          <div
            onClick={() => setActiveTab("Open Order")}
            className={`py-3 px-1 text-sm font-semibold transition-colors cursor-pointer ${
              activeTab === "Open Order"
                ? "border-b-2 border-[#21272A] text-[#21272A] font-bold"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Open Order
          </div>
          <div
            onClick={() => setActiveTab("Order History")}
            className={`py-3 px-1 text-sm font-semibold transition-colors cursor-pointer ${
              activeTab === "Order History"
                ? "border-b-2 border-[#21272A] text-[#21272A] font-bold"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Order History
          </div>
        </nav>
      </div>

      {/* Table */}
      <div className="mt-2 flow-root">
        <div className="-mx-6 -my-2 overflow-x-auto">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="py-3.5 px-3 lg:pl-0 text-left text-xs font-medium uppercase text-gray-400"
                  >
                    Date & Time
                  </th>
                  <th
                    scope="col"
                    className="py-3.5 px-3 text-left text-xs font-medium uppercase text-gray-400 hidden md:table-cell"
                  >
                    Pair
                  </th>
                  <th
                    scope="col"
                    className="py-3.5 px-3 text-left text-xs font-medium uppercase text-gray-400"
                  >
                    Type
                  </th>
                  <th
                    scope="col"
                    className="py-3.5 px-3 text-left text-xs font-medium uppercase text-gray-400 hidden md:table-cell"
                  >
                    Side
                  </th>
                  <th
                    scope="col"
                    className="py-3.5 px-3 text-left text-xs font-medium uppercase text-gray-400"
                  >
                    Price
                  </th>
                  <th
                    scope="col"
                    className="py-3.5 px-3 text-left text-xs font-medium uppercase text-gray-400 hidden md:table-cell"
                  >
                    Amount
                  </th>
                  <th
                    scope="col"
                    className="py-3.5 px-3 text-left text-xs font-medium uppercase text-gray-400 hidden md:table-cell"
                  >
                    Total
                  </th>
                  {activeTab !== "Order History" && (
                    <th
                      scope="col"
                      className="py-3.5 px-3 text-left text-xs font-medium uppercase text-gray-400"
                    >
                      Action
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  [...Array(10)].map((_, idx) => (
                    <tr key={idx} className="border-b bg-white">
                      {[
                        ...Array(
                          isMobile ? 4 : activeTab !== "Order History" ? 8 : 7
                        ),
                      ].map((_, i) => (
                        <td key={i} className="p-4 text-sm">
                          <SkeletonCard
                            type="text"
                            lines={1}
                            widths={["60px"]}
                            heights={["10px"]}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : !data.length ? (
                  <tr>
                    <td
                      colSpan={desktopHeaders.length}
                      className="text-center text-sm py-10 text-gray-500"
                    >
                      No data to display.
                    </td>
                  </tr>
                ) : (
                  data.map((row) => {
                    const rowClass =
                      row.side === "Buy"
                        ? "bg-gradient-to-r from-[#D1E5CE] to-white"
                        : "bg-gradient-to-r from-[#F9DEDC] to-white";
                    const isCurrentRowExpanded = expandedRow === row.id;
                    const mobileBaseHeaders = ["Date & Time", "Type", "Price"];

                    return (
                      <React.Fragment key={row.id}>
                        <tr className={rowClass}>
                          <td className="whitespace-nowrap py-4 px-3 text-sm text-gray-700">
                            {row.datetime}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700 hidden md:table-cell">
                            {row.pair}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
                            {row.type}
                          </td>
                          <td
                            className={`whitespace-nowrap px-3 py-4 text-sm font-semibold hidden md:table-cell ${
                              row.side === "Buy"
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            {row.side}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
                            {row.price}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700 hidden md:table-cell">
                            {row.amount}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700 hidden md:table-cell">
                            {row.total}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
                            <div className="flex items-center gap-2 md:justify-start justify-end">
                              {activeTab === "Open Order" && (
                                <div
                                  onClick={() => onDeleteClick(row.id)}
                                  className="text-gray-400 hover:text-red-600 cursor-pointer"
                                >
                                  <TrashIcon className="w-4 h-4 text-red-700" />
                                </div>
                              )}
                              <div
                                onClick={() => toggleRowExpansion(row.id)}
                                className="text-gray-400 hover:text-gray-600 cursor-pointer md:hidden"
                              >
                                {isCurrentRowExpanded ? (
                                  <ChevronUpIcon className="w-4 h-4" />
                                ) : (
                                  <ChevronDownIcon className="w-4 h-4" />
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                        {isCurrentRowExpanded && (
                          <tr className={`${rowClass} md:hidden`}>
                            <td
                              colSpan={mobileBaseHeaders.length + 1}
                              className="px-3 py-2 text-sm text-gray-700 border-t border-gray-200"
                            >
                              <div className="grid grid-cols-2 gap-y-1">
                                <div>
                                  <span className="font-medium text-gray-500">
                                    Pair:
                                  </span>{" "}
                                  {row.pair}
                                </div>
                                <div>
                                  <span className="font-medium text-gray-500">
                                    Side:
                                  </span>{" "}
                                  <span
                                    className={`${
                                      row.side === "Buy"
                                        ? "text-green-600"
                                        : "text-red-600"
                                    }`}
                                  >
                                    {row.side}
                                  </span>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-500">
                                    Amount:
                                  </span>{" "}
                                  {row.amount}
                                </div>
                                <div>
                                  <span className="font-medium text-gray-500">
                                    Total:
                                  </span>{" "}
                                  {row.total}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="items-center justify-center mt-4 md:mt-6 flex">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};

export default TradeHistoryTable;
